import { endInX9, randomRoundUpCents } from '@/utils/price-transforms';
import { formatPriceForSave } from '@/utils/price-editing';
import {
  getEditWeeklyGrossProfitImpactInMicros,
  getGrossMarginPercentageDeltaByRow,
  getPriceByCostAndMargin,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/variant-pack-cost-change-pricing';
import { getNormalizedPriceInMicros, MICRO_CONVERSION } from '@/utils/money';
import {
  GrossMarginEditAction,
  PriceEditAction,
  PriceEditActionType,
  PricingEdit,
  PricingEditField,
  PricingInvoiceHistoryEntry,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import {
  isValidGrossMarginPercentage,
  isValidPriceInMicros,
} from '@/utils/pricing-validation';
import { isValidNumber } from '@/utils/number';
import { PricingAccountRow } from '@/experiences/sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import { PricingCalculations } from '@/utils/pricing-calculations';

interface PricingEditInput {
  originalRow: PricingAccountRow;
  restaurantUUID: string;
  variantPackUUID: string;
}

interface EffectivePricingEditInput extends PricingEditInput {
  effectiveRow: PricingAccountRow;
}

interface BuildPricingEditParams extends PricingEditInput {
  grossMarginPercentage: number;
  priceInMicros: number;
  updatedField: PricingEditField;
}

interface ComputeActionEditParams extends EffectivePricingEditInput {
  type: PriceEditActionType;
}

interface ComputeExplicitValueEditParams extends PricingEditInput {
  field: PricingEditField;
  value: number;
}

interface ComputeAddAmountEditParams extends EffectivePricingEditInput {
  delta: number;
  field: PricingEditField;
}

export const buildPricingEdit = ({
  grossMarginPercentage,
  originalRow,
  priceInMicros,
  restaurantUUID,
  updatedField,
  variantPackUUID,
}: BuildPricingEditParams): PricingEdit | null => {
  if (
    !isValidGrossMarginPercentage(grossMarginPercentage) ||
    !isValidPriceInMicros(priceInMicros)
  ) {
    return null;
  }

  const grossProfitInMicros = PricingCalculations.grossProfitAmountFromPrice(
    priceInMicros,
    originalRow.costInMicros,
  );

  if (!isValidNumber(grossProfitInMicros)) {
    return null;
  }

  const previousPriceInMicros =
    originalRow.priceInMicros - (originalRow.priceDeltaInMicros ?? 0);

  const grossMarginPercentageDelta = getGrossMarginPercentageDeltaByRow(
    { ...originalRow, grossMarginPercentage },
    previousPriceInMicros,
  );

  const previousGrossProfitInMicros =
    (originalRow.grossProfitInMicros ?? 0) -
    (originalRow.grossProfitDeltaInMicros ?? 0);
  const grossProfitDeltaInMicros =
    grossProfitInMicros - previousGrossProfitInMicros;

  const priceDeltaInMicros = priceInMicros - previousPriceInMicros;

  const grossProfitImpactInMicros = getEditWeeklyGrossProfitImpactInMicros(
    originalRow,
    {
      ...originalRow,
      grossProfitInMicros,
    },
  );

  if (
    !isValidNumber(grossMarginPercentageDelta) ||
    !isValidNumber(grossProfitDeltaInMicros) ||
    !isValidNumber(grossProfitImpactInMicros)
  ) {
    return null;
  }

  return {
    grossMarginPercentage,
    grossMarginPercentageDelta,
    grossProfitDeltaInMicros,
    grossProfitImpactInMicros,
    grossProfitInMicros,
    priceDeltaInMicros,
    priceInMicros,
    restaurantUUID,
    supplierUUID: originalRow.supplierUUID,
    updatedField,
    variantPackUUID,
    weeklyVolume: originalRow.quantity,
  };
};

const applyGrossMarginDelta = (
  params: EffectivePricingEditInput & { delta: number },
): PricingEdit | null => {
  const { delta, effectiveRow, originalRow, restaurantUUID, variantPackUUID } =
    params;
  const newGM = effectiveRow.grossMarginPercentage + delta;
  const newPriceInMicros = getPriceByCostAndMargin(originalRow, newGM);

  if (!isValidNumber(newPriceInMicros)) {
    return null;
  }

  return buildPricingEdit({
    grossMarginPercentage: newGM,
    originalRow,
    priceInMicros: newPriceInMicros,
    restaurantUUID,
    updatedField: PricingEditField.GrossMarginPercentage,
    variantPackUUID,
  });
};

const buildPriceEditFromMicros = (
  params: EffectivePricingEditInput & { newPriceInMicros: number | null },
): PricingEdit | null => {
  const { newPriceInMicros, originalRow, restaurantUUID, variantPackUUID } =
    params;
  const formatted = formatPriceForSave(newPriceInMicros);
  if (!isValidNumber(formatted)) {
    return null;
  }

  const grossProfitInMicros = PricingCalculations.grossProfitAmountFromPrice(
    formatted,
    originalRow.costInMicros,
  );
  const newGM = PricingCalculations.grossMarginPercentFromGrossProfit(
    grossProfitInMicros,
    formatted,
  );

  if (!isValidNumber(newGM)) {
    return null;
  }

  return buildPricingEdit({
    grossMarginPercentage: newGM,
    originalRow,
    priceInMicros: formatted,
    restaurantUUID,
    updatedField: PricingEditField.Price,
    variantPackUUID,
  });
};

// Invoice history is returned most-recent-first, so the first entry with a
// non-null value for the field is the last invoiced value.
export const getLastInvoicePriceInMicros = (
  history: PricingInvoiceHistoryEntry[],
): number | null =>
  history.find((entry) => entry.priceInMicros !== null)?.priceInMicros ?? null;

export const getLastInvoiceGrossMarginPercentage = (
  history: PricingInvoiceHistoryEntry[],
): number | null =>
  history.find((entry) => entry.grossMarginPercentage !== null)
    ?.grossMarginPercentage ?? null;

const applyLastPrice = (
  params: EffectivePricingEditInput,
): PricingEdit | null => {
  const { effectiveRow, originalRow, restaurantUUID, variantPackUUID } = params;
  const lastPriceInMicros = getLastInvoicePriceInMicros(
    originalRow.invoiceHistory,
  );
  if (lastPriceInMicros === null) {
    return null;
  }

  const newPriceInMicros = formatPriceForSave(lastPriceInMicros);
  if (!isValidNumber(newPriceInMicros)) {
    return null;
  }

  if (
    getNormalizedPriceInMicros(newPriceInMicros) ===
    getNormalizedPriceInMicros(effectiveRow.priceInMicros)
  ) {
    return null;
  }

  const grossProfitInMicros = PricingCalculations.grossProfitAmountFromPrice(
    newPriceInMicros,
    originalRow.costInMicros,
  );
  const newGM = PricingCalculations.grossMarginPercentFromGrossProfit(
    grossProfitInMicros,
    newPriceInMicros,
  );

  if (!isValidNumber(newGM)) {
    return null;
  }

  return buildPricingEdit({
    grossMarginPercentage: newGM,
    originalRow,
    priceInMicros: newPriceInMicros,
    restaurantUUID,
    updatedField: PricingEditField.Price,
    variantPackUUID,
  });
};

const applyLastMargin = (
  params: EffectivePricingEditInput,
): PricingEdit | null => {
  const { effectiveRow, originalRow, restaurantUUID, variantPackUUID } = params;
  const lastMargin = getLastInvoiceGrossMarginPercentage(
    originalRow.invoiceHistory,
  );
  if (lastMargin === null) {
    return null;
  }

  if (lastMargin === effectiveRow.grossMarginPercentage) {
    return null;
  }

  const newPriceInMicros = getPriceByCostAndMargin(originalRow, lastMargin);
  if (!isValidNumber(newPriceInMicros)) {
    return null;
  }

  return buildPricingEdit({
    grossMarginPercentage: lastMargin,
    originalRow,
    priceInMicros: newPriceInMicros,
    restaurantUUID,
    updatedField: PricingEditField.GrossMarginPercentage,
    variantPackUUID,
  });
};

const applyEndInX9 = (params: EffectivePricingEditInput): PricingEdit | null =>
  buildPriceEditFromMicros({
    ...params,
    newPriceInMicros: endInX9(params.effectiveRow.priceInMicros),
  });

const applyRandomRoundUp = (
  params: EffectivePricingEditInput,
): PricingEdit | null =>
  buildPriceEditFromMicros({
    ...params,
    newPriceInMicros: randomRoundUpCents(params.effectiveRow.priceInMicros),
  });

export const computeExplicitValueEdit = ({
  field,
  value,
  originalRow,
  restaurantUUID,
  variantPackUUID,
}: ComputeExplicitValueEditParams): PricingEdit | null => {
  if (!isValidNumber(value)) {
    return null;
  }

  if (field === PricingEditField.GrossMarginPercentage) {
    const priceInMicros = getPriceByCostAndMargin(originalRow, value);
    if (!isValidNumber(priceInMicros)) {
      return null;
    }
    return buildPricingEdit({
      grossMarginPercentage: value,
      originalRow,
      priceInMicros,
      restaurantUUID,
      updatedField: PricingEditField.GrossMarginPercentage,
      variantPackUUID,
    });
  }

  const priceInMicros = formatPriceForSave(value * MICRO_CONVERSION);
  if (!isValidNumber(priceInMicros)) {
    return null;
  }

  const grossProfitInMicros = PricingCalculations.grossProfitAmountFromPrice(
    priceInMicros,
    originalRow.costInMicros,
  );
  const newGM = PricingCalculations.grossMarginPercentFromGrossProfit(
    grossProfitInMicros,
    priceInMicros,
  );

  if (!isValidNumber(newGM)) {
    return null;
  }

  return buildPricingEdit({
    grossMarginPercentage: newGM,
    originalRow,
    priceInMicros,
    restaurantUUID,
    updatedField: PricingEditField.Price,
    variantPackUUID,
  });
};

export const computeAddAmountEdit = ({
  field,
  delta,
  effectiveRow,
  originalRow,
  restaurantUUID,
  variantPackUUID,
}: ComputeAddAmountEditParams): PricingEdit | null => {
  if (!isValidNumber(delta)) {
    return null;
  }

  if (field === PricingEditField.GrossMarginPercentage) {
    const newGM = effectiveRow.grossMarginPercentage + delta;
    const priceInMicros = getPriceByCostAndMargin(originalRow, newGM);
    if (!isValidNumber(priceInMicros)) {
      return null;
    }
    return buildPricingEdit({
      grossMarginPercentage: newGM,
      originalRow,
      priceInMicros,
      restaurantUUID,
      updatedField: PricingEditField.GrossMarginPercentage,
      variantPackUUID,
    });
  }

  const formattedDelta = formatPriceForSave(delta * MICRO_CONVERSION);
  if (!isValidNumber(formattedDelta)) {
    return null;
  }
  const newPriceInMicros = effectiveRow.priceInMicros + formattedDelta;

  const grossProfitInMicros = PricingCalculations.grossProfitAmountFromPrice(
    newPriceInMicros,
    originalRow.costInMicros,
  );
  const newGM = PricingCalculations.grossMarginPercentFromGrossProfit(
    grossProfitInMicros,
    newPriceInMicros,
  );

  if (!isValidNumber(newGM)) {
    return null;
  }

  return buildPricingEdit({
    grossMarginPercentage: newGM,
    originalRow,
    priceInMicros: newPriceInMicros,
    restaurantUUID,
    updatedField: PricingEditField.Price,
    variantPackUUID,
  });
};

export const computeActionEdit = ({
  type,
  effectiveRow,
  originalRow,
  restaurantUUID,
  variantPackUUID,
}: ComputeActionEditParams): PricingEdit | null => {
  const pricingEditInput: EffectivePricingEditInput = {
    effectiveRow,
    originalRow,
    restaurantUUID,
    variantPackUUID,
  };

  switch (type) {
    case GrossMarginEditAction.AddOnePercent:
      return applyGrossMarginDelta({ ...pricingEditInput, delta: 1 });
    case GrossMarginEditAction.SubtractOnePercent:
      return applyGrossMarginDelta({ ...pricingEditInput, delta: -1 });
    case PriceEditAction.EndInX9:
      return applyEndInX9(pricingEditInput);
    case PriceEditAction.RandomRoundUp:
      return applyRandomRoundUp(pricingEditInput);
    case PriceEditAction.SetLastPrice:
      return applyLastPrice(pricingEditInput);
    case GrossMarginEditAction.SetLastMargin:
      return applyLastMargin(pricingEditInput);
    // These require an explicit value and are handled via
    // computeExplicitValueEdit / computeAddAmountEdit instead.
    case GrossMarginEditAction.AddAmount:
    case GrossMarginEditAction.SetAmount:
    case PriceEditAction.AddAmount:
    case PriceEditAction.SetAmount:
      return null;
    default:
      return null;
  }
};
